import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Trash2, ShoppingBag, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { deals } from '../data/mockData';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const { user, isAuthenticated, loading, removeFavorite, isFavorite } = useAuth();
  const navigate = useNavigate();
  const [favoriteDeals, setFavoriteDeals] = useState([]);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
      toast.error('Please sign in to view your favorites');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.favorites) {
      const userFavorites = deals.filter(deal => user.favorites.includes(deal.id));
      setFavoriteDeals(userFavorites);
    }
  }, [user?.favorites]);

  const handleRemoveFavorite = async (dealId) => {
    setRemoving(dealId);
    try {
      await removeFavorite(dealId);
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-gray-800 transition-colors">Home</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">My Favorites</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <Heart className="text-black" size={24} fill="currentColor" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Favorites</h1>
              <p className="text-gray-600 text-sm">
                {favoriteDeals.length} {favoriteDeals.length === 1 ? 'deal' : 'deals'} saved
              </p>
            </div>
          </div>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-800 hover:underline text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to Deals
          </Link>
        </div>

        {/* Favorites Grid */}
        {favoriteDeals.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favoriteDeals.map((deal, index) => (
              <div 
                key={deal.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group"
                style={{ 
                  animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`
                }}
              >
                {/* Image */}
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  <img 
                    src={deal.image} 
                    alt={deal.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  
                  {/* Discount Badge */}
                  {deal.discount && (
                    <span className="absolute top-3 left-3 bg-black text-white text-xs font-bold px-2 py-1 rounded">
                      {deal.discount}
                    </span>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemoveFavorite(deal.id)}
                    disabled={removing === deal.id}
                    className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors group/btn"
                    data-testid={`remove-favorite-${deal.id}`}
                  >
                    {removing === deal.id ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 size={16} className="text-black group-hover/btn:scale-110 transition-transform" />
                    )}
                  </button>
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Store */}
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    {deal.store}
                  </p>
                  
                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[2.5rem]">
                    {deal.title}
                  </h3>

                  {/* Pricing */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg font-bold text-gray-800">
                      ${deal.salePrice?.toFixed(2)}
                    </span>
                    {deal.originalPrice && (
                      <span className="text-sm text-gray-400 line-through">
                        ${deal.originalPrice.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Coupon Code */}
                  {deal.couponCode && (
                    <div className="flex items-center gap-2 mb-3 bg-yellow-50 px-3 py-2 rounded-lg">
                      <Tag size={14} className="text-yellow-600" />
                      <span className="text-sm font-mono font-bold text-yellow-700">
                        {deal.couponCode}
                      </span>
                    </div>
                  )}

                  {/* Action Button */}
                  <Link
                    to={`/store/${deal.store}`}
                    className="w-full flex items-center justify-center gap-2 bg-[#000000] text-white py-2.5 rounded-lg font-medium hover:bg-[#000000] transition-colors"
                  >
                    <ShoppingBag size={16} />
                    View Deal
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={40} className="text-gray-300" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No favorites yet</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Start saving deals you love by clicking the heart icon on any deal card. 
              Your favorites will appear here for easy access.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#000000] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#000000] transition-colors"
            >
              <ShoppingBag size={18} />
              Browse Deals
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default FavoritesPage;
